if (!customElements.get('cart-discount-code')) {
  class CartDiscountCode extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.appliedCodes = [];
      this.panel = this.querySelector('.cart-sidebar-discount__panel');
      this.triggerButton = this.querySelector('.cart-sidebar-discount__trigger');
      this.codesList = this.querySelector('.cart-sidebar-discount__codes-list');
      this.input = this.querySelector('.cart-sidebar-discount__input');
      this.applyButton = this.querySelector('.cart-sidebar-discount__apply');
      this.errorElement = this.querySelector('.cart-sidebar-discount__error');

      this.onTriggerClick = () => {
        this.setPanelOpen(!this.classList.contains('is-open'));
      };

      this.onApplyClick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.onApply();
      };

      this.onCodesListClick = (event) => {
        const removeButton = event.target.closest('[data-remove-index]');
        if (!removeButton) return;

        event.preventDefault();
        event.stopPropagation();
        this.onRemoveCode(Number(removeButton.dataset.removeIndex));
      };

      this.onInputKeydown = (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.onApply();
        }
      };

      this.onInputInput = () => {
        this.toggleApplyState();
      };

      this.triggerButton?.addEventListener('click', this.onTriggerClick);
      this.applyButton?.addEventListener('click', this.onApplyClick);
      this.codesList?.addEventListener('click', this.onCodesListClick);
      this.input?.addEventListener('keydown', this.onInputKeydown);
      this.input?.addEventListener('input', this.onInputInput);

      this.toggleApplyState();
      this.scheduleSyncFromCart();

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event.source === 'cart-discount-code') return;
          this.syncFromCart();
        });
      }
    }

    disconnectedCallback() {
      this.triggerButton?.removeEventListener('click', this.onTriggerClick);
      this.applyButton?.removeEventListener('click', this.onApplyClick);
      this.codesList?.removeEventListener('click', this.onCodesListClick);
      this.input?.removeEventListener('keydown', this.onInputKeydown);
      this.input?.removeEventListener('input', this.onInputInput);

      if (this.cartUpdateUnsubscriber) {
        this.cartUpdateUnsubscriber();
        this.cartUpdateUnsubscriber = undefined;
      }
    }

    getCartUrl() {
      const cartUrl = window.routes?.cart_url || '/cart';
      return cartUrl.replace(/\/$/, '');
    }

    getCartUpdateUrl() {
      return window.routes?.cart_update_url || '/cart/update.js';
    }

    scheduleSyncFromCart() {
      const runSync = () => {
        this.syncFromCart();
      };

      if (window.routes?.cart_url) {
        runSync();
        return;
      }

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', runSync, { once: true });
        return;
      }

      window.requestAnimationFrame(runSync);
    }

    setPanelOpen(isOpen) {
      this.classList.toggle('is-open', isOpen);
      this.triggerButton?.setAttribute('aria-expanded', isOpen ? 'true' : 'false');

      if (isOpen) {
        window.requestAnimationFrame(() => this.input?.focus());
      }
    }

    toggleApplyState() {
      if (!this.applyButton || !this.input) return;
      this.applyButton.disabled = this.input.value.trim().length === 0;
    }

    setLoading(isLoading) {
      if (!this.applyButton) return;

      if (isLoading) {
        this.applyButton.disabled = true;
        this.applyButton.classList.add('is-loading');
        this.applyButton.innerHTML = `<span class="visually-hidden">${this.getLabel('applyingLabel')}</span><span class="cart-sidebar-discount__loader" aria-hidden="true"></span>`;
      } else {
        this.applyButton.classList.remove('is-loading');
        this.applyButton.textContent = this.getLabel('applyLabel');
        this.toggleApplyState();
      }
    }

    setError(message) {
      if (this.errorElement) {
        this.errorElement.textContent = message ? this.decodeHtmlEntities(message) : '';
      }
      if (message) this.setPanelOpen(true);
    }

    decodeHtmlEntities(value) {
      if (!value) return value;

      const element = document.createElement('textarea');
      element.innerHTML = value;
      return element.value;
    }

    getLabel(name, fallback = '') {
      return this.decodeHtmlEntities(this.dataset[name] || fallback);
    }

    normalizeCode(code) {
      return code.trim().toLowerCase();
    }

    looksLikeDiscountCode(value) {
      const trimmed = String(value || '').trim();
      if (!trimmed) return false;

      return !(trimmed.includes(' ') && trimmed.length > 20);
    }

    getAppliedCodesFromCart(cart) {
      const codes = [];
      const seen = new Set();

      const addCode = (code) => {
        const trimmedCode = String(code || '').trim();
        const normalizedCode = this.normalizeCode(trimmedCode);
        if (!trimmedCode || !this.looksLikeDiscountCode(trimmedCode) || seen.has(normalizedCode)) {
          return;
        }

        seen.add(normalizedCode);
        codes.push(trimmedCode);
      };

      if (Array.isArray(cart.discount_codes)) {
        cart.discount_codes.forEach((entry) => {
          if (!entry?.code || entry.applicable === false) return;

          if (entry.applicable === true) {
            addCode(entry.code);
            return;
          }

          addCode(entry.code);
        });
      }

      if (!codes.length && Array.isArray(cart.cart_level_discount_applications)) {
        cart.cart_level_discount_applications.forEach((application) => {
          if (application?.type === 'discount_code' && application.title) {
            addCode(application.title);
          }
        });
      }

      return codes;
    }

    formatInvalidForCartMessage(code) {
      const template = this.getLabel('invalidForCartLabel', "{{ code }} isn't valid for this cart.");
      return template.replace(/\{\{\s*code\s*\}\}/gi, code);
    }

    looksLikeApiErrorMessage(entryCode, attemptedCode) {
      const message = entryCode.trim();
      if (!message) return false;

      return (
        this.normalizeCode(message) !== this.normalizeCode(attemptedCode) &&
        (message.includes(' ') || message.length > 40)
      );
    }

    getDiscountErrorMessage(cart, attemptedCode, previousCodes, fallback) {
      if (!Array.isArray(cart.discount_codes) || !attemptedCode) return fallback;

      const normalizedAttempt = this.normalizeCode(attemptedCode);
      const previousNormalized = new Set((previousCodes || []).map((entry) => this.normalizeCode(entry)));

      const rejected = cart.discount_codes.filter((entry) => entry.applicable === false && entry.code);

      if (!rejected.length) return fallback;

      const exactMatch = rejected.find((entry) => this.normalizeCode(entry.code) === normalizedAttempt);
      if (exactMatch) {
        return this.formatInvalidForCartMessage(attemptedCode);
      }

      const newRejections = rejected.filter(
        (entry) => !previousNormalized.has(this.normalizeCode(entry.code))
      );

      if (newRejections.length) {
        const apiErrorEntry = newRejections.find((entry) =>
          this.looksLikeApiErrorMessage(entry.code, attemptedCode)
        );
        if (apiErrorEntry) return apiErrorEntry.code.trim();

        return this.formatInvalidForCartMessage(attemptedCode);
      }

      const firstRejected = rejected[0];
      if (this.looksLikeApiErrorMessage(firstRejected.code, attemptedCode)) {
        return firstRejected.code.trim();
      }

      return this.formatInvalidForCartMessage(firstRejected.code);
    }

    isCodeValidOnCart(cart, code) {
      if (!Array.isArray(cart.discount_codes)) return false;

      const normalizedCode = this.normalizeCode(code);
      return cart.discount_codes.some(
        (entry) =>
          entry.applicable === true &&
          entry.code &&
          this.normalizeCode(entry.code) === normalizedCode
      );
    }

    setAppliedCodes(codes) {
      const uniqueCodes = [];
      const seen = new Set();

      codes.forEach((code) => {
        const trimmedCode = code.trim();
        const normalizedCode = this.normalizeCode(trimmedCode);
        if (!trimmedCode || seen.has(normalizedCode)) return;
        seen.add(normalizedCode);
        uniqueCodes.push(trimmedCode);
      });

      this.appliedCodes = uniqueCodes;
      this.classList.toggle('has-codes', uniqueCodes.length > 0);
      this.renderAppliedCodes();
    }

    escapeHtml(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    renderAppliedCodes() {
      if (!this.codesList) return;

      const removeLabelTemplate = this.getLabel('removeCodeLabel', 'Remove {{ code }}');

      this.codesList.innerHTML = this.appliedCodes
        .map((code, index) => {
          const safeCode = this.escapeHtml(code);
          const removeLabel = this.escapeHtml(removeLabelTemplate.replace('{{ code }}', code));
          return `
            <li class="cart-sidebar-discount__code-chip">
              <span class="cart-sidebar-discount__code-value">${safeCode}</span>
              <button
                type="button"
                class="cart-sidebar-discount__code-remove"
                data-remove-index="${index}"
                aria-label="${removeLabel}"
              >&times;</button>
            </li>
          `;
        })
        .join('');
    }

    async fetchCart() {
      const response = await fetch(`${this.getCartUrl()}.js`);
      if (!response.ok) throw new Error('Cart fetch failed');
      return response.json();
    }

    async updateCartSections() {
      if (this.dataset.context !== 'drawer') return;

      const response = await fetch(`${this.getCartUrl()}?section_id=cart-drawer`);
      if (!response.ok) return;

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const selectors = ['cart-drawer-items', '.cart-drawer__footer'];

      selectors.forEach((selector) => {
        const targetElement = document.querySelector(selector);
        const sourceElement = html.querySelector(selector);
        if (targetElement && sourceElement) {
          targetElement.replaceWith(sourceElement);
        }
      });

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-discount-code' });
      }
    }

    async applyDiscountCodes(codes) {
      const payload = { discount: codes.length ? codes.join(',') : '' };

      const response = await fetch(this.getCartUpdateUrl(), {
        ...fetchConfig(),
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error('Discount update failed');
      return response.json();
    }

    async onApply() {
      const code = this.input?.value.trim();

      if (!code) {
        this.setError(this.getLabel('invalidLabel'));
        return;
      }

      if (this.appliedCodes.some((appliedCode) => this.normalizeCode(appliedCode) === this.normalizeCode(code))) {
        this.setError(this.getLabel('alreadyAppliedLabel'));
        return;
      }

      this.setError('');
      this.setLoading(true);

      const previousCodes = [...this.appliedCodes];
      const nextCodes = [...this.appliedCodes, code];

      try {
        const cart = await this.applyDiscountCodes(nextCodes);

        if (this.isCodeValidOnCart(cart, code)) {
          this.setAppliedCodes(this.getAppliedCodesFromCart(cart));
          if (this.input) this.input.value = '';
          this.setPanelOpen(false);
          await this.updateCartSections();
        } else {
          await this.applyDiscountCodes(previousCodes);
          const revertedCart = await this.fetchCart();
          this.setAppliedCodes(this.getAppliedCodesFromCart(revertedCart));
          this.setError(this.getDiscountErrorMessage(cart, code, previousCodes, this.getLabel('invalidLabel')));
        }
      } catch (error) {
        console.error(error);
        try {
          await this.applyDiscountCodes(previousCodes);
          const revertedCart = await this.fetchCart();
          this.setAppliedCodes(this.getAppliedCodesFromCart(revertedCart));
        } catch (revertError) {
          console.error(revertError);
        }
        this.setError(this.getLabel('errorLabel'));
      } finally {
        this.setLoading(false);
      }
    }

    async onRemoveCode(codeIndex) {
      const codeToRemove = this.appliedCodes[codeIndex];
      if (!codeToRemove) return;

      this.setError('');
      const nextCodes = this.appliedCodes.filter((_, index) => index !== codeIndex);

      try {
        await this.applyDiscountCodes(nextCodes);
        const cart = await this.fetchCart();
        this.setAppliedCodes(this.getAppliedCodesFromCart(cart));
        if (this.input) this.input.value = '';
        this.toggleApplyState();

        if (nextCodes.length === 0) {
          this.setPanelOpen(true);
        } else {
          this.setPanelOpen(false);
        }

        await this.updateCartSections();
      } catch (error) {
        console.error(error);
        this.setError(this.getLabel('errorLabel'));
      }
    }

    async syncFromCart() {
      try {
        const cart = await this.fetchCart();
        this.setAppliedCodes(this.getAppliedCodesFromCart(cart));
      } catch (error) {
        console.error(error);
      }
    }
  }

  customElements.define('cart-discount-code', CartDiscountCode);
}
